import { ROLE_KEYS, ROLE_LABEL_KEY } from './labels'

/**
 * Map an RBAC role name (e.g. "DONOR", "ADVISOR", "CHARITY", "SUPPORT_ADMIN",
 * "SYSTEM_ADMIN") to one of our context role-key buckets. Multiple admin
 * variants collapse to ROLE_KEYS.ADMIN.
 */
function rbacRoleToKey(rbacRoleName) {
  if (!rbacRoleName) return null
  const upper = rbacRoleName.toUpperCase()
  if (upper === 'DONOR') return ROLE_KEYS.DONOR
  if (upper === 'ADVISOR') return ROLE_KEYS.ADVISOR
  if (upper === 'CHARITY') return ROLE_KEYS.CHARITY
  if (upper === 'SUPPORT_ADMIN' || upper === 'SYSTEM_ADMIN') return ROLE_KEYS.ADMIN
  return null
}

function compareByNumber(a, b) {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

/**
 * Build the list of contexts the user can switch into.
 *
 * Per the v2 review (Mike + Tammy + Cristie, May 1) the switcher operates at
 * portal-mode + distinct-entity granularity, NOT at per-client granularity:
 *
 *   - One row per fund the user OWNS (DONOR role)        -> fund context
 *   - One row per CharityUser link (distinct charity)    -> charity context
 *   - ONE row "Advisor Portal" if the user has any
 *     ADVISOR role on any fund (collapsed, not per-client) -> advisor context
 *   - ONE row "Administration" if the user has any
 *     SUPPORT_ADMIN or SYSTEM_ADMIN role                  -> admin context
 *
 * An advisor's individual client funds appear as TILES inside the advisor
 * dashboard. The switcher does not double-count them.
 *
 * Each context: { kind, id, role, roleLabelKey, displayName, fundNumber, homeRoute }
 */
// Charity invite status 2 = accepted. Other statuses (1=invited, 3=revoked, 4=unconfirmed)
// mean the user has not yet completed setup and should not see the charity portal yet.
const CHARITY_INVITE_ACCEPTED = 2

export function computeContexts({ me } = {}) {
  if (!me) return []

  const userRoles = me.userRoles || []
  // Only surface charityUsers whose invite has been accepted (or who have no invite at all,
  // covering legacy users who predate the invite system).
  const charityUsers = (me.charityUsers || []).filter(
    (cu) => !cu.charityInvite || Number(cu.charityInvite.status) === CHARITY_INVITE_ACCEPTED
  )
  // Suppress the auto-created `charity-<charityId>` fund from showing as a Donor
  // switcher row. The auto-fund is a side effect of the charity signup flow, not a
  // real donor context in the user's mental model. This applies unconditionally —
  // a reassigned charity portal may land on a user with no CharityUser record yet,
  // so gating on charityUsers.length would still let it slip through as a donor row.
  const ownedFunds = (me.funds || []).filter(
    (f) => !f?.closed && !(typeof f?.name === 'string' && f.name.startsWith('charity-'))
  )

  // Build a set of open portal types from fundProfiles for quick lookup
  const openPortalTypes = new Set((me.fundProfiles || []).filter((p) => !p.closed).map((p) => p.fundType))

  const contexts = []
  const seenFundIds = new Set()
  const seenCharityIds = new Set()
  let adminAdded = false
  let advisorAdded = false

  userRoles.forEach((ur) => {
    const roleKey = rbacRoleToKey(ur.role?.name)

    // DONOR role on a fund -> per-fund row
    if (ur.fundId && roleKey === ROLE_KEYS.DONOR) {
      if (seenFundIds.has(ur.fundId)) return
      if (ur.fund?.closed) return
      if (ur.fund?.name?.startsWith('charity-')) return
      seenFundIds.add(ur.fundId)

      const fundFromRole = ur.fund
      const ownedFund = ownedFunds.find((f) => f.id === ur.fundId)
      const fundName =
        ownedFund?.fundProfile?.portalName || fundFromRole?.name || ownedFund?.name || `Fund #${ur.fundId}`
      const fundNumber = ownedFund?.number || null

      contexts.push({
        kind: 'fund',
        id: ur.fundId,
        role: ROLE_KEYS.DONOR,
        roleLabelKey: ROLE_LABEL_KEY[ROLE_KEYS.DONOR],
        displayName: fundName,
        fundNumber,
        homeRoute: `/funds/${ur.fundId}`,
        hasDonorTaskAccess: false,
      })
      return
    }

    // ADVISOR role on a fund -> single collapsed "Advisor Portal" row
    // (clients appear as tiles inside the advisor dashboard, not as switcher rows)
    if (ur.fundId && roleKey === ROLE_KEYS.ADVISOR && !advisorAdded) {
      if (!openPortalTypes.has('advisor')) return
      advisorAdded = true
      contexts.push({
        kind: 'advisor',
        id: 'portal',
        role: ROLE_KEYS.ADVISOR,
        roleLabelKey: ROLE_LABEL_KEY[ROLE_KEYS.ADVISOR],
        displayName: 'Advisor Portal',
        fundNumber: null,
        homeRoute: '/funds',
        hasDonorTaskAccess: false,
      })
      return
    }

    // CHARITY role -> needs CharityUser link to know which charity
    if (roleKey === ROLE_KEYS.CHARITY && !ur.fundId) {
      charityUsers.forEach((cu) => {
        if (!cu.charityId || seenCharityIds.has(cu.charityId)) return
        // Check if this specific charity's fund portal is closed
        const charityFund = (me.funds || []).find((f) => f.name === `charity-${cu.charityId}`)
        if (charityFund?.closed) return
        seenCharityIds.add(cu.charityId)
        contexts.push({
          kind: 'charity',
          id: cu.charityId,
          role: ROLE_KEYS.CHARITY,
          roleLabelKey: ROLE_LABEL_KEY[ROLE_KEYS.CHARITY],
          displayName: cu.charity?.accountName || cu.charity?.name || `Charity ${cu.charityId}`,
          fundNumber: cu.charityId || null,
          homeRoute: `/charities/${cu.charityId}`,
          hasDonorTaskAccess: false,
        })
      })
      return
    }

    // Admin role (no fundId) -> single Administration row
    if (roleKey === ROLE_KEYS.ADMIN && !ur.fundId && !adminAdded) {
      if (!openPortalTypes.has('admin')) return
      adminAdded = true
      contexts.push({
        kind: 'admin',
        id: null,
        role: ROLE_KEYS.ADMIN,
        roleLabelKey: ROLE_LABEL_KEY[ROLE_KEYS.ADMIN],
        displayName: 'Administration',
        fundNumber: null,
        homeRoute: '/management',
        hasDonorTaskAccess: false,
      })
    }
  })

  // Backstop: legacy owned funds without RbacUserRole rows still surface as donor contexts.
  ownedFunds.forEach((f) => {
    if (seenFundIds.has(f.id)) return
    seenFundIds.add(f.id)
    contexts.push({
      kind: 'fund',
      id: f.id,
      role: ROLE_KEYS.DONOR,
      roleLabelKey: ROLE_LABEL_KEY[ROLE_KEYS.DONOR],
      displayName: f.fundProfile?.portalName || f.name,
      fundNumber: f.number || null,
      homeRoute: `/funds/${f.id}`,
      hasDonorTaskAccess: false,
    })
  })

  // Backstop: charity users without a CHARITY RbacUserRole row still surface as charity contexts.
  charityUsers.forEach((cu) => {
    if (!cu.charityId || seenCharityIds.has(cu.charityId)) return
    const charityFund = (me.funds || []).find((f) => f.name === `charity-${cu.charityId}`)
    if (charityFund?.closed) return
    seenCharityIds.add(cu.charityId)
    contexts.push({
      kind: 'charity',
      id: cu.charityId,
      role: ROLE_KEYS.CHARITY,
      roleLabelKey: ROLE_LABEL_KEY[ROLE_KEYS.CHARITY],
      displayName: cu.charity?.accountName || cu.charity?.name || `Charity ${cu.charityId}`,
      fundNumber: cu.charityId || null,
      homeRoute: `/charities/${cu.charityId}`,
      hasDonorTaskAccess: false,
    })
  })

  // Sort donor (fund) rows by fund number and charity rows by charity number.
  // Sort is stable and only compares within the same kind, so relative order
  // between different kinds (fund/charity/advisor/admin) is left untouched.
  contexts.sort((a, b) => {
    if (a.kind !== b.kind) return 0
    if (a.kind === 'fund') return compareByNumber(a.fundNumber, b.fundNumber)
    if (a.kind === 'charity') return compareByNumber(a.id, b.id)
    return 0
  })

  return contexts
}

const PORTAL_TYPE_LABEL = {
  fund: 'Donor',
  charity: 'Charity',
  advisor: 'Advisor',
  admin: 'Administrator',
}

/**
 * Splits the portal description into { name, typeLine }:
 *  - name: the portal/fund/charity's display name (may be null, e.g. an advisor
 *    or admin portal with no portalName set)
 *  - typeLine: "<Portal Type> Portal", with a ": <Charity Name> <Charity Number>"
 *    suffix for charity portals
 * Returns null when the portal/fund can't be resolved yet.
 */
export function resolvePortalPhraseParts(activePortal, me) {
  if (!activePortal || !me) return null

  const portalType = PORTAL_TYPE_LABEL[activePortal.kind]
  if (!portalType) return null

  if (activePortal.kind === 'fund') {
    const fund = (me.funds || []).find((f) => f.id === activePortal.id)
    const portalName = fund?.fundProfile?.portalName || fund?.name
    if (!portalName) return null
    return { name: portalName, typeLine: `${portalType} Portal` }
  }

  if (activePortal.kind === 'charity') {
    const charityUser = (me.charityUsers || []).find((cu) => cu.charityId === String(activePortal.id))
    const charityName = charityUser?.charity?.accountName
    if (!charityName) return null
    return { name: charityName, typeLine: `${portalType} Portal: ${charityName} ${activePortal.id}` }
  }

  // Advisor / Admin portals aren't tied to a specific fund, but still have a
  // userId-scoped FundProfile that may carry a portalName.
  const fundProfile = (me.fundProfiles || []).find((p) => p.fundType === activePortal.kind && p.fundId == null)
  const portalName = fundProfile?.portalName
  return { name: portalName || null, typeLine: `${portalType} Portal` }
}

/**
 * Detect the active context from the URL pathname.
 * Returns { kind, id } or null.
 */
export function detectActiveContext(pathname) {
  if (!pathname) return null
  if (pathname.startsWith('/management')) return { kind: 'admin', id: null }
  const charityMatch = pathname.match(/^\/charities\/([^/]+)/)
  if (charityMatch) return { kind: 'charity', id: charityMatch[1] }
  // /advisor/* = advisor portal pages (e.g. /advisor/my-profile)
  if (pathname.startsWith('/advisor')) return { kind: 'advisor', id: 'portal' }
  // /funds = advisor dashboard (no fund id), /funds/N = specific fund
  if (pathname === '/funds') return { kind: 'advisor', id: 'portal' }
  const fundMatch = pathname.match(/^\/funds\/(\d+)/)
  if (fundMatch) return { kind: 'fund', id: Number(fundMatch[1]) }
  const profileMatch = pathname.match(/^\/profile\/(\d+)/)
  if (profileMatch) return { kind: 'fund', id: Number(profileMatch[1]) }
  return null
}
